import React, { useEffect, useRef, useState } from "react";
import { useStore } from "react-redux";
import InfoCard from "../../common/report/InfoCard";
import pumpIcon from "../../../assets/images/determine-pump.svg";
import DepletionCurveGraph from "../../calculation-section/basiccalculations/charts/depletion-curve";
import IPRCurveGraph from "../../calculation-section/basiccalculations/charts/wellInfo-curve";
import { LABELS } from "../../../constants/labels/jetlift/jetlift.labels";
import { useAppSelector } from "../../../store/hooks/StoreHooks";
import { columns, defaultDepletionData, defaultIPRData, tabs } from "../../../constants/jetlift/jetlift";
import TableComponent from "../../common/TableComponent";
import { getJetliftMainReportDataFromUtility } from "../../../utilities/commonUtilities";
import { RootState } from "../../../store/ConfigureStore";
import HorizontalTab from "../../common/HorizontalTab/horizontalTab";

const SizingSummary: React.FC = () => {

    const { showDepletionPlotInMainReport, showIPRPlotInMainReport, iprCurve, depletionCurve } = useAppSelector((state) => state?.jetlift?.basicCalculations);
    const { jetliftProjectID } = useAppSelector((state) => state?.createProject);
    const store = useStore<RootState>();
    const [activeTab, setActiveTab] = useState(0);
    const [reportInfo, setReportInfo] = useState<any>(null);
    const iprChartRef = useRef<HTMLDivElement | null>(null);
    const depletionChartRef = useRef<HTMLDivElement | null>(null);
    const MainReportData = () => {
        const sateData = store.getState();
        let mainReportData = getJetliftMainReportDataFromUtility(sateData);
        setReportInfo(mainReportData?.inputs);
    };

    useEffect(() => {
        if (jetliftProjectID?.projectID) {
            MainReportData();
        }
    }, [jetliftProjectID?.projectID]);

    const performanceCurveData = {
        inputPropertiesGridOne: [
            {
                label: `${LABELS?.perforationDepth}`,
                value: `${reportInfo?.perforation !== "NaN" ? reportInfo?.perforation : ""} ${reportInfo?.ft_unit}`,
            },
            {
                label: `${LABELS?.pumpVerticalDepth}`,
                value: `${reportInfo?.tvd !== "NaN" ? reportInfo?.tvd : ""} ${reportInfo?.ft_unit}`,
            },
            {
                label: `${LABELS?.pumpInstallation}`,
                value: `${reportInfo?.pumpInstallation !== "NaN" ? reportInfo?.pumpInstallation : LABELS?.casingInstallation}`,
            },
            {
                label: `${LABELS?.casingProdId}`,
                value: `${reportInfo?.CasingID !== "NaN" ? reportInfo?.CasingID : ""} ${reportInfo?.in_unit}`,
            },
            {
                label: `${LABELS?.powerTubingId}`,
                value: `${reportInfo?.tubing1_TubingID !== "NaN" ? reportInfo?.tubing1_TubingID : ""} ${reportInfo?.in_unit}`
            },
        ],
        inputPropertiesGridTwo: [
            {
                label: `${LABELS?.powerTubingOD}`,
                value: `${reportInfo?.tubing1_TubingOD !== "NaN" ? reportInfo?.tubing1_TubingOD : ""} ${reportInfo?.in_unit}`,
            },
            {
                label: `${LABELS?.tubingLength}`,
                value: `${reportInfo?.settingDepth !== "NaN" ? reportInfo?.settingDepth : ""} ${reportInfo?.ft_unit}`,
            },
            {
                label: `${LABELS?.pipeRoughness}`,
                value: `${reportInfo?.pipeRoughness !== "NaN" ? reportInfo?.pipeRoughness : "0.0018"}`,
            },
            {
                label: `${LABELS?.oilGravity}`,
                value: `${reportInfo?.oilAPI !== "NaN" ? reportInfo?.oilAPI : ""}`,
            },
            {
                label: `${LABELS?.producedVolWaterCut}`,
                value: `${reportInfo?.waterCut !== "NaN" ? reportInfo?.waterCut : ""} ${reportInfo?.percentage_unit}`,
            },
            {
                label: `${LABELS?.waterSpecificGravity}`,
                value: `${reportInfo?.spGrWater !== "NaN" ? reportInfo?.spGrWater : ""}`,
            },
            {
                label: `${LABELS?.producingGOR}`,
                value: `${reportInfo?.Gas_Intake_gor !== "NaN" ? reportInfo?.Gas_Intake_gor : ""}`,
            }
        ],
        inputPropertiesGridThree: [
            {
                label: `${LABELS?.gasSpGravity}`,
                value: `${reportInfo?.specificGravityGas !== "NaN" ? reportInfo?.specificGravityGas : ""}`,
            },
            {
                label: `${LABELS?.wellHeadPress}`,
                value: `${reportInfo?.flowLinePressure !== "NaN" ? reportInfo?.flowLinePressure : ""} ${reportInfo?.psi_unit}`,
            },
            {
                label: `${LABELS?.wellStaticBHP}`,
                value: `${reportInfo?.staticBHP !== "NaN" ? reportInfo?.staticBHP : ""} ${reportInfo?.psi_unit}`,
            },
            {
                label: `${LABELS?.pumpIntakePress}`,
                value: `${reportInfo?.pumpIntakePressure !== "NaN" ? reportInfo?.pumpIntakePressure: ""} ${LABELS?.psia}`,
            },
            {
                label: `${LABELS?.wellTestFlowRate}`,
                value: `${reportInfo?.testFlowRate !== "NaN" ? reportInfo?.testFlowRate : ""} blpd`,
            },
            {
                label: `${LABELS?.wellHeadTemp}`,
                value: `${reportInfo?.surfaceTemperature !== "NaN" ? reportInfo?.surfaceTemperature : ""} ${reportInfo?.temp_unit}`,
            },
        ],
        inputPropertiesGridFour: [
            {
                label: `${LABELS?.bottomHoleTemp}`,
                value: `${reportInfo?.bottomHoleTemperature !== "NaN" ? reportInfo?.bottomHoleTemperature : ""} ${reportInfo?.temp_unit}`,
            },
            {
                label: `${LABELS?.gasVented}`,
                value: `${reportInfo?.gasVent !== "NaN" ? reportInfo?.gasVent : ""} ${reportInfo?.percentage_unit}`,
            },
            {
                label: `${LABELS?.powerFluidOilWater}`,
                value: `${reportInfo?.powerFluidOilWater ? reportInfo?.powerFluidOilWater : "Oil"}`,
            },
            {
                label: `${LABELS?.powerFluidAPI}`,
                value: `${reportInfo?.powerFluidOilWater === "Oil" ? reportInfo?.oilAPI ? reportInfo?.oilAPI : "" : reportInfo?.spGrWater ? reportInfo?.spGrWater : ""}`,
            },
            {
                label: `${LABELS?.bubblePoint}`,
                value: `${reportInfo?.bubblePointPressure !== "NaN" ? reportInfo?.bubblePointPressure : ""} psia`,
            },
            // {
            //     label: `${LABELS?.wellHeadPress}`,
            //     value: `${reportInfo?.flowLinePressure ? reportInfo?.flowLinePressure : ""} ${reportInfo?.in_unit}`,
            // },
        ],
        pumpSummaryGridOne: [
            {
                label: `${LABELS?.targetProductionRate}: ${reportInfo?.desiredProductionRate ? reportInfo?.desiredProductionRate : ""} ${LABELS?.blpd} @ ${LABELS?.pumpIntakePressure}: ${reportInfo?.pumpIntakePressure ? reportInfo?.pumpIntakePressure : ""} ${LABELS?.psia}`,
                value: "",
            },
            {
                label: "",
                value: ""
            },
            {
                label: "",
                value: ""
            },
        ],
        pumpSummaryGridTwo: [
            {
                label: `${LABELS?.predictedSurfacePowerFluidInjectionPressure}`,
                value: `${reportInfo?.surfacePowerFluidInjectionPressure !== "NaN" ? reportInfo?.surfacePowerFluidInjectionPressure : ""} psia`,
            },
            {
                label: `${LABELS?.predictedSurfacePowerFluidInjectionRate}`,
                value: `${reportInfo?.surfacePowerFluidInjectionRate !== "NaN" ? reportInfo?.surfacePowerFluidInjectionRate : ""} blpd`
            },
            {
                label: `${LABELS?.predictedPumpIntakePressure}`,
                value: `${reportInfo?.pumpIntakePressure !== "NaN" ? reportInfo?.pumpIntakePressure : ""} psia`
            },
        ],
        pumpSummaryGridThree: [
            {
                label: `${LABELS?.predictedPumpDischargePressure}`,
                value: `${reportInfo?.pumpDischargePressure !== "NaN" ? reportInfo?.pumpDischargePressure : ""} psia`,
            },
            {
                label: `${LABELS?.predictedPowerFluidPressureAtPump}`,
                value: `${reportInfo?.powerFluidPressureAtPump !== "NaN" ? reportInfo?.powerFluidPressureAtPump : ""} psia`
            },
            {
                label: `${LABELS?.predictedHorsePowerRequirement}`,
                value: `${reportInfo?.horsePowerRequirement !== "NaN" ? reportInfo?.horsePowerRequirement : ""} HP`
            },
        ],
        inputDataGridOne: [
            {
                label: `${LABELS?.reservoirPressure}`,
                value: `${reportInfo?.reservoirPressure !== "NaN" ? reportInfo?.reservoirPressure : ""} ${reportInfo?.psi_unit}`,
            },
            {
                label: `${LABELS?.waterCut} (fraction)`,
                value: `${reportInfo?.waterCut !== "NaN" ? (Number(reportInfo?.waterCut)/100)?.toFixed(2) : ""}`
            },
            {
                label: `${LABELS?.testFlowRate}`,
                value: `${reportInfo?.testFlowRate !== "NaN" ? reportInfo?.testFlowRate : ""} ${reportInfo?.bpd_unit}`,
            }
        ],
        inputDataGridTwo: [
            {
                label: `${LABELS?.flowingBtmHolePressure} (psia)`,
                value: `${reportInfo?.flowingBHP !== "NaN" ? reportInfo?.flowingBHP : ""}`,
            },
            {
                label: `Override ${LABELS?.bubblePoint} (psia)`,
                value: `${reportInfo?.bubblePointPressure !== "NaN" ? reportInfo?.bubblePointPressure : ""}`
            },
        ],
        inputDataGridThree: [
            {
                label: `${LABELS?.calcualtedPIatDataPoint}`,
                value: `${reportInfo?.productivityIndex !== "NaN" ? reportInfo?.productivityIndex : ""}`,
            },
            {
                label: `${LABELS?.tangent} (stbf/psi)`,
                value: ""
            },
        ]
    };

    return (
        <>
            <div className="determine-widget-containers">
                <div className='header-main border-b-[1px] border-darkcolor-400'>
                    <div className='flex flex-row justify-between'>
                        <div>
                            <h3>DETERMINE sizing summary</h3>
                            <h5>
                                The sizing summary provides an overview of the Jet lift design's
                                key components, along with the downhole and operating
                                conditions.
                            </h5>
                        </div>
                    </div>
                </div>
                <div className='container-body flex flex-col gap-2'>
                    <div className='flex flex-col gap-2 pb-4 mb-4'>
                        <h5>Project name</h5>
                        <h1 className='header-title'>{reportInfo?.ProjectName}</h1>
                    </div>
                    <div className='flex flex-row gap-4'>
                        <div className='flex flex-col gap-4 basis-full lg:basis-1/2'>
                            <InfoCard
                                title={LABELS?.casingTubing}
                                items={{
                                    "Casing": `${reportInfo?.Casing ? reportInfo?.Casing : ""} `,
                                    "Tubing": `${reportInfo?.Tubing1 ? reportInfo?.Tubing1 : ""}`,
                                    "Flow line pressure": `${reportInfo?.flowLinePressure ? reportInfo?.flowLinePressure : ""} ${reportInfo?.psi_unit}`,
                                }}
                                showSeparator={true}
                            />
                            <InfoCard
                                title={LABELS?.equipment}
                                items={{
                                    "Nozzle": `${reportInfo?.nozzleDiameterSize ? reportInfo?.nozzleDiameterSize : ""} ${reportInfo?.in_unit}`,
                                    "Throat": `${reportInfo?.throatDiameterSize ? reportInfo?.throatDiameterSize : ""} ${reportInfo?.in_unit}`,
                                    "Surface pump size": `${reportInfo?.selected_surface_pump_size ? reportInfo?.selected_surface_pump_size : ""}`,
                                    "Plunger size": `${reportInfo?.selected_plunget_size ? reportInfo?.selected_plunget_size : ""}`,
                                    "Downhole equipment size": "",
                                    "Minimum ID": "",
                                }}
                                showSeparator={true}
                            />
                        </div>
                        <div className='hidden lg:flex basis-1/2'>
                            <img src={pumpIcon} id='pump-icon' alt='pump icon' />
                        </div>
                    </div>
                    <div className='flex flex-col gap-4'>
                        <HorizontalTab tabs={tabs} tabChange={setActiveTab} />
                        {activeTab === 0 &&
                            <InfoCard
                                title={LABELS?.wellDepth}
                                items={{
                                    "Pump setting MD": `${reportInfo?.settingDepth ? reportInfo?.settingDepth : ""} ${reportInfo?.ft_unit}`,
                                    "Pump setting VD": `${reportInfo?.tvd ? reportInfo?.tvd : ""} ${reportInfo?.ft_unit}`,
                                    "DATUM": `${reportInfo?.perforation ? reportInfo?.perforation : ""} ${reportInfo?.ft_unit}`,
                                }}
                                bgColor="bg-darkcolor-700"
                                showSeparator={true}
                            />}
                        {activeTab === 1 &&
                            <InfoCard
                                title={LABELS?.wellIprDatum}
                                items={{
                                    "PI": `${reportInfo?.productivityIndex !== "NaN" ? reportInfo?.productivityIndex : ""} ${reportInfo?.bpd_unit}/${reportInfo?.psi_unit}`,
                                    "Static BHP": `${reportInfo?.staticBHP !== "NaN" ? reportInfo?.staticBHP : ""} ${reportInfo?.psi_unit}`,
                                    "Flowing BHP": `${reportInfo?.flowingBHP !== "NaN" ? reportInfo?.flowingBHP : ""} ${reportInfo?.psi_unit}`,
                                    "Static FL": `${reportInfo?.staticFL !== "NaN" ? reportInfo?.staticFL : ""} ${reportInfo?.ft_unit}`,
                                    "Producing FL": `${reportInfo?.producingFL !== "NaN" ? reportInfo?.producingFL : ""} ${reportInfo?.ft_unit}`,
                                }}
                                bgColor="bg-darkcolor-700"
                                showSeparator={true}
                            />}
                        {activeTab === 2 &&
                            <InfoCard
                                title={LABELS?.operatingPerformance}
                                items={{
                                    "Production rate": `${reportInfo?.desiredProductionRate ? reportInfo?.desiredProductionRate : ""} ${reportInfo?.bpd_unit}`,
                                    "BOPD": `${reportInfo?.bopd ? reportInfo?.bopd : ""} ${reportInfo?.bpd_unit}`,
                                    "BWPD": `${reportInfo?.bwpd ? reportInfo?.bwpd : ""} ${reportInfo?.bpd_unit}`,
                                    "Gas production": `${reportInfo?.gasProduction ? reportInfo?.gasProduction : ""}  ${reportInfo?.gasProduction_unit}`,
                                    "PIP": `${reportInfo?.Gas_Intake_pip !== "NaN" ? reportInfo?.Gas_Intake_pip : ""} ${reportInfo?.psi_unit}`
                                }}
                                bgColor="bg-darkcolor-700"
                                showSeparator={true}
                            />}
                        {activeTab === 3 &&
                            <InfoCard
                                title={LABELS?.fluidProperties}
                                items={{
                                    "Regional PVT": `${reportInfo?.region ? reportInfo?.region : ""}`,
                                    "Gas gravity": `${reportInfo?.specificGravityGas ? reportInfo?.specificGravityGas : ""}`,
                                    "Oil API° / SP": `${reportInfo?.oilAPI ? reportInfo?.oilAPI : ""} / ${reportInfo?.specificGravityGas ? reportInfo?.specificGravityGas : ""}`,
                                    "Bubble point": `${reportInfo?.bubblePointPressure ? reportInfo?.bubblePointPressure : ""} ${reportInfo?.psi_unit}`,
                                    "Water gravity": `${reportInfo?.spGrWater ? reportInfo?.spGrWater : ""}`,
                                    "GOR": `${reportInfo?.gor ? reportInfo?.gor : ""} ${reportInfo?.gor_unit}`,
                                    "Water cut": `${reportInfo?.waterCut ? reportInfo?.waterCut : ""} ${reportInfo?.percentage_unit}`,
                                    "GLR": `${reportInfo?.glr ? reportInfo?.glr : ""} ${reportInfo?.glr_unit}`,
                                    "Bottom hole temp": `${reportInfo?.bottomHoleTemperature ? reportInfo?.bottomHoleTemperature : ""} ${reportInfo?.temp_unit}`,
                                    "Fluid composite SG": `${reportInfo?.fluidViscosity ? reportInfo?.fluidViscosity : ""}`,
                                    "Surface fluid temp": `${reportInfo?.surfaceTemperature ? reportInfo?.surfaceTemperature : ""} ${reportInfo?.temp_unit}`,
                                    "Liquid phase SG": `${reportInfo?.liquidPhaseCombinedSpGr !== "NaN" ? reportInfo?.liquidPhaseCombinedSpGr : ""}`,
                                }}
                                bgColor="bg-darkcolor-700"
                                showSeparator={true}
                            />}
                        {activeTab === 4 &&
                            <InfoCard
                                title={LABELS?.chemicalInjectionPerformance}
                                items={{
                                    "Total volume @ intake": `${reportInfo?.Gas_Intake_tfv !== "NaN" ? reportInfo?.Gas_Intake_tfv : ""} ${reportInfo?.bpd_unit}`,
                                    "Oil rate": `${reportInfo?.Gas_Intake_oilVolume ? reportInfo?.Gas_Intake_oilVolume : ""} ${reportInfo?.bpd_unit}`,
                                    "Water rate": `${reportInfo?.Gas_Intake_waterVolume ? reportInfo?.Gas_Intake_waterVolume : ""} ${reportInfo?.bpd_unit}`,
                                    "Gas rate": `${reportInfo?.Gas_Intake_gasVolume !== "NaN" ? reportInfo?.Gas_Intake_gasVolume : ""} ${reportInfo?.bpd_unit}`,
                                    "Total system TDH": ` ${reportInfo?.ft_unit}`,
                                }}
                                bgColor="bg-darkcolor-700"
                                showSeparator={true}
                            />}
                    </div>
                </div>
            </div>
            {(showDepletionPlotInMainReport || showIPRPlotInMainReport) && <div className="determine-widget-containers">
                <div className="header-main">
                    <div className="flex flex-row justify-between">
                        <div>
                            <h3 className="text-[18px] font-semibold text-neutral-25">DETERMINE well inflow & depletion curve</h3>
                            <h5>Projected performance for the Jet lift design.</h5>
                        </div>
                    </div>
                </div>
                <div className="container-body border-t border-dark flex flex-col gap-2">
                    <div className="flex flex-col gap-2 pb-4 mb-4">
                        <div className="mb-[16px]" />
                        <div className="pl-[200px] pr-[200px]">
                            {showIPRPlotInMainReport && <div className="flex justify-center">
                                <div>
                                    <p className="text-[18px] font-semibold text-neutral-25 ">
                                        {LABELS?.wellInflowPerformance}
                                    </p>
                                </div>
                            </div>}
                            <div className="mb-[15px] pt-[30px]" ref={iprChartRef}>
                                {showIPRPlotInMainReport && <IPRCurveGraph data={iprCurve ? iprCurve : defaultIPRData} />}
                            </div>
                            {showDepletionPlotInMainReport && (
                                <>
                                    <div className="flex justify-center">
                                        <div>
                                            <p className="text-[18px] font-semibold text-neutral-25">
                                                {LABELS?.depletionCurve}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="mb-[15px] pt-[30px]" ref={depletionChartRef}>
                                        <DepletionCurveGraph data={depletionCurve ?? defaultDepletionData} />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            }
            <div className="determine-widget-containers">
                <div className='header-main border-b-[1px] border-darkcolor-400'>
                    <div className='flex flex-row justify-between'>
                        <div>
                            <h3>DETERMINE performance curve</h3>
                            <h5>Projected performance for the Jet lift design.</h5>
                        </div>
                    </div>
                </div>
                <div className="flex flex-col gap-5 px-6 py-5">
                    <div className="border-b-[1px] border-darkcolor-400">
                        <p className="text-[16px] text-primary-400 font-bold pb-5">{LABELS?.inputProperties}</p>
                    </div>
                    <div className="grid grid-cols-4 gap-4">
                        <div className="flex flex-col gap-4">
                            {performanceCurveData?.inputPropertiesGridOne?.map((data, id) => (
                                <div className="flex justify-between text-neutral-25" key={id}>
                                    <label>{data?.label}</label>
                                    <label>{data?.value}</label>
                                </div>
                            ))}
                        </div>
                        <div className="flex flex-col gap-4">
                            {performanceCurveData?.inputPropertiesGridTwo?.map((data, id) => (
                                <div className="flex justify-between text-neutral-25" key={id}>
                                    <label>{data?.label}</label>
                                    <label>{data?.value}</label>
                                </div>
                            ))}
                        </div>
                        <div className="flex flex-col gap-4">
                            {performanceCurveData?.inputPropertiesGridThree?.map((data, id) => (
                                <div className="flex justify-between text-neutral-25" key={id}>
                                    <label>{data?.label}</label>
                                    <label>{data?.value}</label>
                                </div>
                            ))}
                        </div>
                        <div className="flex flex-col gap-4">
                            {performanceCurveData?.inputPropertiesGridFour?.map((data, id) => (
                                <div className="flex justify-between text-neutral-25" key={id}>
                                    <label>{data?.label}</label>
                                    <label>{data?.value}</label>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="flex flex-col gap-5 px-6 py-5">
                    <div className="border-b-[1px] border-darkcolor-400">
                        <p className="text-[16px] text-primary-400 font-bold pb-5">{LABELS?.pumpPerformanceSummary} @ {LABELS?.nozzle} {`(${reportInfo?.nozzleDiameterSize})`} {LABELS?.and} {LABELS?.throat} {`(${reportInfo?.throatDiameterSize})`} {LABELS?.diameters}</p>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                        <div className="flex flex-col gap-4">
                            {performanceCurveData?.pumpSummaryGridOne?.map((data, id) => (
                                <div className="flex justify-between items-center text-neutral-25" key={id}>
                                    <label>{data?.label}</label>
                                    <label>{data?.value}</label>
                                </div>
                            ))}
                        </div>
                        <div className="flex flex-col gap-4">
                            {performanceCurveData?.pumpSummaryGridTwo?.map((data, id) => (
                                <div className="flex justify-between items-center text-neutral-25" key={id}>
                                    <label>{data?.label}</label>
                                    <label>{data?.value}</label>
                                </div>
                            ))}
                        </div>
                        <div className="flex flex-col gap-4">
                            {performanceCurveData?.pumpSummaryGridThree?.map((data, id) => (
                                <div className="flex justify-between items-center text-neutral-25" key={id}>
                                    <label>{data?.label}</label>
                                    <label>{data?.value}</label>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="flex flex-col gap-5 px-6 py-5">
                    <div className="border-b-[1px] border-darkcolor-400"></div>
                    <TableComponent data={reportInfo?.formattedBasicSummaryScenarios || []} columns={columns} rowsPerPage={1} showHeader={false} tableClass="" />
                    <div className="border-b-[1px] border-darkcolor-400"></div>
                    <div className="grid grid-cols-3 gap-4">
                        <div className="flex flex-col gap-4">
                            {performanceCurveData?.inputDataGridOne?.map((data, id) => (
                                <div className="flex justify-between text-neutral-25" key={id}>
                                    <label>{data?.label}</label>
                                    <label>{data?.value}</label>
                                </div>
                            ))}
                        </div>
                        <div className="flex flex-col gap-4">
                            {performanceCurveData?.inputDataGridTwo?.map((data, id) => (
                                <div className="flex justify-between text-neutral-25" key={id}>
                                    <label>{data?.label}</label>
                                    <label>{data?.value}</label>
                                </div>
                            ))}
                        </div>
                        <div className="flex flex-col gap-4">
                            {performanceCurveData?.inputDataGridThree?.map((data, id) => (
                                <div className="flex justify-between text-neutral-25" key={id}>
                                    <label>{data?.label}</label>
                                    <label>{data?.value}</label>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SizingSummary;
